// Location: src/features/devices/components/DeviceList.tsx

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  List,
  TextField,
  ReferenceField,
  ReferenceInput,
  FunctionField,
  useTranslate,
  useDataProvider,
  ListContextProvider,
  useListContext,
  useList,
} from 'react-admin';
import { useWatch, useFormContext } from 'react-hook-form';
import {
  Chip,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Alert,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import CustomDatagrid from '@/components/common/CustomDatagrid';
import { CustomSelectInputFilter, CustomTextInputFilter } from '@/components/common/CustomFilters';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FilterPopoverProvider, RaFilterPopover } from '@/components/common/RaFilterPopover';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { useHotelScope } from '@/hooks/useHotelScope';
import { devicesHelpConfig } from '@/config/helpConfig';
import { useDevicePermissions } from '../hooks/useDevicePermissions';
import DeviceActions from './DeviceActions';
import {
  getDeviceTypeColor,
  getDeviceStatusColor,
  getDeviceDisplayName,
} from '@/types/models';
import type { Device } from '@/types/models';
import { compactListSx } from '@/components/common/compactListSx';
import { getCurrentUser } from '@/utils/auth';
import { humanizeMetadataKey } from '../config/metadataKeys';

/**
 * Metadata Search Dialog Component
 */
interface MetadataSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSearch: (key: string, value: string) => void;
}

const MetadataSearchDialog = ({ open, onClose, onSearch }: MetadataSearchDialogProps) => {
  const translate = useTranslate();
  const [metadataKey, setMetadataKey] = useState('');
  const [metadataValue, setMetadataValue] = useState('');
  const [error, setError] = useState('');

  const handleSearch = () => {
    if (!metadataKey.trim()) {
      setError(translate('resources.events.metadata.keyRequired'));
      return;
    }
    if (!metadataValue.trim()) {
      setError(translate('resources.events.metadata.valueRequired'));
      return;
    }
    setError('');
    onSearch(metadataKey.trim(), metadataValue.trim());
    onClose();
  };

  const handleClose = () => {
    setMetadataKey('');
    setMetadataValue('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('resources.devices.filters.metadataSearch')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <MuiTextField
            label={translate('resources.devices.filters.metadataKey')}
            value={metadataKey}
            onChange={(e) => setMetadataKey(e.target.value)}
            placeholder="firmwareVersion"
            size="small"
            fullWidth
          />
          <MuiTextField
            label={translate('resources.devices.filters.metadataValue')}
            value={metadataValue}
            onChange={(e) => setMetadataValue(e.target.value)}
            placeholder="2.1.0"
            size="small"
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info">
            {translate('resources.devices.filters.metadataSearchHint')}
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{translate('common.actions.cancel')}</Button>
        <Button onClick={handleSearch} variant="contained" startIcon={<SearchIcon />}>
          {translate('common.actions.search')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Metadata search button — placed inside the CollapsibleFilterPanel
 */
const MetadataSearchButton = ({ onClick, active }: { onClick: () => void; active: boolean }) => {
  const translate = useTranslate();
  return (
    <Button
      onClick={onClick}
      size="small"
      variant={active ? 'contained' : 'outlined'}
      startIcon={<SearchIcon sx={{ fontSize: '14px !important' }} />}
      sx={{
        fontSize: '0.75rem',
        py: 0.3,
        px: 1.5,
        minHeight: '30px',
        borderRadius: '16px',
        textTransform: 'none',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        ...(active
          ? {
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': { backgroundColor: 'primary.dark' },
            }
          : {
              borderColor: 'grey.300',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
              },
            }),
      }}
    >
      {translate('resources.devices.filters.metadataSearch')}
    </Button>
  );
};

/**
 * Hotel filter that dynamically reacts to the selected group in the filter form.
 * Uses useWatch to observe groupId changes and re-fetch hotels accordingly.
 */
const HotelFilterInput = ({ label, sx, isMultiGroupAdmin }: { label: string; sx?: any; isMultiGroupAdmin?: boolean }) => {
  const groupId = useWatch({ name: 'groupId' });
  const { setValue } = useFormContext();
  const prevGroupIdRef = useRef(groupId);
  const user = getCurrentUser();
  // Multi-group admin with no group selected → show all hotels (no groupId filter)
  const effectiveGroupId = groupId || (isMultiGroupAdmin ? undefined : user?.groupId);

  // Clear hotel selection when group changes
  useEffect(() => {
    if (prevGroupIdRef.current !== groupId) {
      setValue('hotelId', '');
      prevGroupIdRef.current = groupId;
    }
  }, [groupId, setValue]);

  return (
    <ReferenceInput
      source="hotelId"
      reference="hotels"
      sort={{ field: 'name', order: 'ASC' }}
      filter={effectiveGroupId ? { groupId: effectiveGroupId } : undefined}
      alwaysOn
    >
      <CustomSelectInputFilter
        label={label}
        optionText="name"
        sx={sx}
      />
    </ReferenceInput>
  );
};

/**
 * Device List Component
 *
 * Displays all devices with filtering, sorting, and pagination.
 * Permissions:
 * - SuperAdmin: All devices + create/edit/delete
 * - GroupAdmin: Devices in their groups + export
 * - HotelAdmin: Devices in their hotels (read-only)
 * - Receptionist: Devices in their hotels (read-only)
 */
/** Pre-fills the List's own `hotelId` filter when the shared scope selector
 *  narrows to exactly one hotel, without touching `DeviceFilters`/RaFilterPopover. */
const SyncDeviceHotelFilter = ({ hotelId }: { hotelId: string | null }) => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  useEffect(() => {
    if (hotelId && filterValues.hotelId !== hotelId) {
      setFilters({ ...filterValues, hotelId }, displayedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);
  return null;
};

export const DeviceList = ({ embedded }: { embedded?: boolean }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const { canEdit, canCreate, canExport } = useDevicePermissions();
  const user = getCurrentUser();
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';
  const isGroupAdmin = user?.role === 'GroupAdmin';
  const assignedGroups = user?.assignedGroups ?? [];
  const isMultiGroupAdmin = isGroupAdmin && assignedGroups.length > 1;
  const showGroupFilter = isSuperAdmin || isMultiGroupAdmin;

  // Shared multi-hotel scope selector (Decision 6/unit G, wave 3 follow-up): an
  // ADDITIONAL control alongside the existing hotel/group filter popover
  // fields. A subset (or the whole scope) is sent as `hotelIds` via the
  // List's permanent `filter` prop below; a single-hotel selection also
  // pre-fills the existing `hotelId` filter field (`SyncDeviceHotelFilter`)
  // so both stay coherent.
  const scope = useHotelScope();
  const scopeHotelIds = scope.selectedHotelIds && scope.selectedHotelIds.length > 0
    ? scope.selectedHotelIds.join(',')
    : undefined;
  const singleScopeHotelId = scope.selectedHotelIds && scope.selectedHotelIds.length === 1
    ? scope.selectedHotelIds[0]
    : null;

  // Metadata search state
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [metadataSearchActive, setMetadataSearchActive] = useState(false);
  const [metadataKey, setMetadataKey] = useState('');
  const [metadataValue, setMetadataValue] = useState('');
  const [metadataSearchResults, setMetadataSearchResults] = useState<Device[]>([]);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);

  // Handle metadata search
  const handleMetadataSearch = useCallback(async (key: string, value: string) => {
    setMetadataKey(key);
    setMetadataValue(value);
    setMetadataSearchActive(true);
    setIsLoadingMetadata(true);

    try {
      const response = await dataProvider.searchDevicesByMetadata({ key, value });
      setMetadataSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching devices by metadata:', error);
      setMetadataSearchResults([]);
    } finally {
      setIsLoadingMetadata(false);
    }
  }, [dataProvider]);

  // Clear metadata search
  const handleClearMetadataSearch = useCallback(() => {
    setMetadataSearchActive(false);
    setMetadataKey('');
    setMetadataValue('');
    setMetadataSearchResults([]);
  }, []);

  // List context for metadata search results
  const metadataListContext = useList({
    data: metadataSearchResults,
    isLoading: isLoadingMetadata,
    perPage: 25,
  });

  // CSV export (standalone header)
  const handleExport = useCallback(async () => {
    try {
      const { data } = await dataProvider.getList('devices', {
        filter: {},
        sort: { field: 'registeredAt', order: 'DESC' },
        pagination: { page: 1, perPage: 10000 },
      });
      if (!data?.length) return;
      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row =>
          headers.map(h => JSON.stringify((row as Record<string, unknown>)[h] ?? '')).join(',')
        ),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'devices-export.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    }
  }, [dataProvider]);

  const filterWidth = { width: { xs: '100%', sm: 180 } };

  // Filters shown in the popover (everything except the free-text search, which
  // lives inline in the header).
  const popoverFilters = useMemo(() => (
    <>
      {/* Search */}
      <CustomTextInputFilter
        source="q"
        label={translate('resources.devices.filters.search')}
        alwaysOn
        sx={filterWidth}
      />

      {/* Hotel filter — dynamically scoped by selected group */}
      <HotelFilterInput
        label={translate('resources.devices.filters.hotel')}
        sx={filterWidth}
        isMultiGroupAdmin={isMultiGroupAdmin}
      />
      {showGroupFilter && (
        <ReferenceInput
          source="groupId"
          reference="groups"
          sort={{ field: 'name', order: 'ASC' }}
          filter={isMultiGroupAdmin ? { id: assignedGroups } : {}}
          alwaysOn
        >
          <CustomSelectInputFilter
            label={translate('resources.devices.filters.group')}
            optionText="name"
          />
        </ReferenceInput>
      )}

      {/* Classification filters */}
      <CustomSelectInputFilter
        source="deviceType"
        alwaysOn
        label={translate('resources.devices.filters.deviceType')}
        choices={[
          { id: 'Kiosk', name: translate('resources.devices.enums.deviceType.kiosk') },
          { id: 'Dispenser', name: translate('resources.devices.enums.deviceType.dispenser') },
          { id: 'Mobile', name: translate('resources.devices.enums.deviceType.mobile') },
        ]}
      />
      <CustomSelectInputFilter
        source="channel"
        alwaysOn
        label={translate('resources.devices.filters.channel')}
        choices={[
          { id: 'Kiosk', name: translate('resources.devices.enums.channel.kiosk') },
          { id: 'Dispenser', name: translate('resources.devices.enums.channel.dispenser') },
          { id: 'Online', name: translate('resources.devices.enums.channel.online') },
          { id: 'AirLobby', name: translate('resources.devices.enums.channel.airLobby') },
          { id: 'MobileKiosk', name: translate('resources.devices.enums.channel.mobileKiosk') },
        ]}
      />

      {/* State filters */}
      <CustomSelectInputFilter
        source="status"
        alwaysOn
        label={translate('resources.devices.filters.status')}
        choices={[
          { id: 'Active', name: translate('resources.devices.enums.status.active') },
          { id: 'Inactive', name: translate('resources.devices.enums.status.inactive') },
          { id: 'PendingActivation', name: translate('resources.devices.enums.status.pendingactivation') },
        ]}
      />
      <CustomSelectInputFilter
        source="source"
        alwaysOn
        label={translate('resources.devices.filters.source')}
        choices={[
          { id: 'Updater', name: translate('resources.devices.enums.source.updater') },
          { id: 'Manual', name: translate('resources.devices.enums.source.manual') },
        ]}
      />

      {/* Special search */}
      <MetadataSearchButton
        onClick={() => setMetadataDialogOpen(true)}
        active={metadataSearchActive}
      />
    </>
  ), [translate, metadataSearchActive, showGroupFilter, isMultiGroupAdmin, assignedGroups]);

  // Shared datagrid content
  const renderDatagrid = () => (
    <CustomDatagrid
      rowClick={canEdit ? 'edit' : false}
      bulkActionButtons={false}
      primarySource="installationCode"
      secondarySource="hotelId"
    >
      {/* Actions column */}
      <FunctionField
        label=""
        sortable={false}
        render={(record: Device) => <DeviceActions record={record} />}
      />

      {/* Installation Code + Name */}
      <FunctionField
        source="installationCode"
        label={translate('resources.devices.fields.installationCode')}
        sortable={true}
        render={(record: Device) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {getDeviceDisplayName(record)}
          </Typography>
        )}
      />

      {/* Hotel Name */}
      <ReferenceField
        source="hotelId"
        reference="hotels"
        label={translate('resources.devices.fields.hotel')}
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>

      {/* Group Name (optional) */}
      <ReferenceField
        source="groupId"
        reference="groups"
        label={translate('resources.devices.fields.group')}
        link={false}
        emptyText={translate('common.notAssigned')}
      >
        <TextField source="name" />
      </ReferenceField>

      {/* Device Type with badge */}
      <FunctionField
        label={translate('resources.devices.fields.deviceType')}
        sortable={true}
        sortBy="deviceType"
        render={(record: Device) => (
          <Chip
            label={translate(`resources.devices.enums.deviceType.${record.deviceType.toLowerCase()}`)}
            color={getDeviceTypeColor(record.deviceType)}
            size="small"
          />
        )}
      />

      {/* Status with badge */}
      <FunctionField
        label={translate('resources.devices.fields.status')}
        sortable={true}
        sortBy="status"
        render={(record: Device) => (
          <Chip
            label={translate(`resources.devices.enums.status.${record.status.toLowerCase()}`)}
            color={getDeviceStatusColor(record.status)}
            size="small"
          />
        )}
      />

      {/* Metadata presence indicator (F-B-015): the full pairs moved to the
          device detail (DeviceEdit.tsx); the list shows only a compact count
          with a tooltip listing the humanised keys. */}
      <FunctionField
        label={translate('resources.devices.fields.metadata')}
        sortable={false}
        render={(record: Device) => {
          const entries = record.metadata ? Object.entries(record.metadata) : [];
          if (entries.length === 0) {
            return <Typography component="span" color="text.secondary">-</Typography>;
          }
          return (
            <Tooltip
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.5 }}>
                  {entries.map(([key, value]) => (
                    <Typography key={key} variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {humanizeMetadataKey(key, translate)}: {String(value)}
                    </Typography>
                  ))}
                </Box>
              }
              arrow
              placement="top"
            >
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ cursor: 'default' }}
                onClick={(e) => e.stopPropagation()}
              >
                {translate('resources.devices.metadataCount', { smart_count: entries.length })}
              </Typography>
            </Tooltip>
          );
        }}
      />
    </CustomDatagrid>
  );

  // If metadata search is active, show results in a ListContextProvider
  if (metadataSearchActive) {
    return (
      <>
        <MetadataSearchDialog
          open={metadataDialogOpen}
          onClose={() => setMetadataDialogOpen(false)}
          onSearch={handleMetadataSearch}
        />
        <Box sx={{ p: 2 }}>
          <Alert
            severity="info"
            sx={{ mb: 1, py: 0, fontSize: '0.75rem', '& .MuiAlert-message': { padding: '4px 0' } }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleClearMetadataSearch}
                startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: '0.7rem' }}
              >
                {translate('common.actions.clear')}
              </Button>
            }
          >
            {`${metadataKey} = ${metadataValue}`}
          </Alert>
          <ListContextProvider value={metadataListContext}>
            {renderDatagrid()}
          </ListContextProvider>
        </Box>
      </>
    );
  }

  const list = (
    <List
      sx={compactListSx}
      filterDefaultValues={{}}
      filter={scopeHotelIds ? { hotelIds: scopeHotelIds } : undefined}
      storeKey={isMultiGroupAdmin ? 'devices-multigroup' : 'devices'}
      actions={false}
      sort={{ field: 'registeredAt', order: 'DESC' }}
      perPage={25}
      title={translate('resources.devices.list.title')}
      empty={false}
    >
      <SyncDeviceHotelFilter hotelId={singleScopeHotelId} />
      <RaFilterPopover searchSource="q">{popoverFilters}</RaFilterPopover>
      {renderDatagrid()}
    </List>
  );

  if (embedded) {
    return (
      <>
        <MetadataSearchDialog
          open={metadataDialogOpen}
          onClose={() => setMetadataDialogOpen(false)}
          onSearch={handleMetadataSearch}
        />
        {list}
      </>
    );
  }

  return (
    <FilterPopoverProvider>
      <MetadataSearchDialog
        open={metadataDialogOpen}
        onClose={() => setMetadataDialogOpen(false)}
        onSearch={handleMetadataSearch}
      />
      <Box sx={{ p: { xs: 1, sm: 1 } }}>
        <SectionHeader manualScreen="devices"
          helpConfig={devicesHelpConfig}
          left={<HotelScopeSelector scope={scope} />}
          useRaFilters
          showCreate={canCreate}
          onCreateClick={() => navigate('/devices/create')}
          showExport={canExport}
          onExportClick={handleExport}
        />
        {list}
      </Box>
    </FilterPopoverProvider>
  );
};

export default DeviceList;